import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map } from 'immutable';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Paper, Select, MenuItem } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { pairUp } from '../Commissioning/utils';

type FieldDef = { key: string; options?: string[] };

const YES_NO = ['Yes', 'No'];
const COMM_ARCH_OPTIONS = ['Direct Ethernet', 'TCP-Serial', 'TCP-Radio', 'Cellular', 'Other'];
const PROTOCOL_OPTIONS = ['RTU', 'ASCII'];

const PROJECT_INFO_FIELDS: FieldDef[] = [
  { key: 'project' },
  { key: 'location' },
  { key: 'commLoopNumber' },
];

const NETWORK_SWITCH_FIELDS: FieldDef[] = [
  { key: 'connectionType', options: COMM_ARCH_OPTIONS },
  { key: 'switchNameId' },
  { key: 'portNumber' },
  { key: 'cabinetId' },
  { key: 'buildingNumber' },
  { key: 'vlanId' },
  { key: 'portConfigVerified', options: YES_NO },
];

const PROTOCOL_CONVERTER_FIELDS: FieldDef[] = [
  { key: 'pcArchitectureType', options: COMM_ARCH_OPTIONS },
  { key: 'pcMakeModel' },
  { key: 'pcDeviceTag' },
  { key: 'pcIpAddress' },
  { key: 'pcSubnetMask' },
  { key: 'pcGateway' },
  { key: 'pcMacAddress' },
  { key: 'pcProtocol', options: PROTOCOL_OPTIONS },
  { key: 'pcSerialConfig' },
  { key: 'pcCommVerified', options: YES_NO },
];

const CELLULAR_ROUTER_FIELDS: FieldDef[] = [
  { key: 'crMakeModel' },
  { key: 'crDeviceTag' },
  { key: 'crIpAddress' },
  { key: 'crSubnetMask' },
  { key: 'crAntennaType' },
  { key: 'crRadioStrength' },
  { key: 'crCommVerified', options: YES_NO },
];

const MASTER_RADIO_FIELDS: FieldDef[] = [
  { key: 'mrRadioType' },
  { key: 'mrCommLoopNumber' },
  { key: 'mrRadioTag' },
  { key: 'mrRadId' },
  { key: 'mrNetworkId' },
  { key: 'mrRfChannel' },
  { key: 'mrApplication' },
  { key: 'mrNetworkConfig' },
  { key: 'mrNetworkSpeed' },
  { key: 'mrSerialConfig' },
  { key: 'mrAntennaType' },
  { key: 'mrRadioStrength' },
  { key: 'mrPartNumber' },
  { key: 'mrManufacturer' },
];

const SLAVE_RADIO_FIELDS: FieldDef[] = [
  { key: 'srControlPanels' },
  { key: 'srControllerTypes' },
  { key: 'srControlModules' },
  { key: 'srModbusAddresses' },
  { key: 'srActiveCircuits' },
  { key: 'srDistanceToMaster' },
];

function renderInput (field: FieldDef, value: string, disabled: boolean, onChange: (key: string, val: string) => void) {
  if (field.options) {
    return (
      <Select
        fullWidth
        disabled={disabled}
        value={value || ''}
        displayEmpty
        onChange={(e) => onChange(field.key, e.target.value as string)}
      >
        <MenuItem value=''><em>-</em></MenuItem>
        {field.options.map((opt) => (
          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
        ))}
      </Select>
    );
  }
  return (
    <TextField
      fullWidth
      disabled={disabled}
      value={value || ''}
      onChange={(e) => onChange(field.key, e.target.value)}
    />
  );
}

function InfoSection ({ titleKey, fields, sectionKey, translationPrefix, data, onChange, readOnly, isMobile }) {
  const { t } = useTranslation();
  const [sectionData, setSectionData] = useState(Map(data?.get(sectionKey) || {}));

  useEffect(() => {
    setSectionData(Map(data?.get(sectionKey) || {}));
  }, [data, sectionKey]);

  const onFieldChange = (key: string, val: string) => {
    const updated = sectionData.set(key, val);
    setSectionData(updated);
    onChange(['extras', sectionKey, key], val);
  };

  const pairs = pairUp(fields);

  return (
    <Paper sx={{ marginBottom: '30px' }}>
      <h3>{t(titleKey)}</h3>
      <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className='sub-headers'>{t('common.field')}</TableCell>
              <TableCell className='sub-headers'>{t('common.value')}</TableCell>
              <TableCell className='sub-headers'>{t('common.field')}</TableCell>
              <TableCell className='sub-headers'>{t('common.value')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(pairs as [FieldDef, FieldDef | undefined][]).map(([fieldA, fieldB]) => (
              <TableRow key={fieldA.key}>
                <TableCell className='sub-headers'>{t(`${translationPrefix}.${fieldA.key}`)}</TableCell>
                <TableCell className='body-cell'>
                  {renderInput(fieldA, sectionData.get(fieldA.key) || '', readOnly, onFieldChange)}
                </TableCell>
                {fieldB ? (
                  <>
                    <TableCell className='sub-headers'>{t(`${translationPrefix}.${fieldB.key}`)}</TableCell>
                    <TableCell className='body-cell'>
                      {renderInput(fieldB, sectionData.get(fieldB.key) || '', readOnly, onFieldChange)}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell />
                    <TableCell />
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

const ALL_SECTIONS = [
  { titleKey: 'commissioningCommLoop.projectInfo.title', fields: PROJECT_INFO_FIELDS, sectionKey: 'projectInfo', translationPrefix: 'commissioningCommLoop.projectInfo' },
  { titleKey: 'commissioningCommLoop.networkSwitch.title', fields: NETWORK_SWITCH_FIELDS, sectionKey: 'networkSwitch', translationPrefix: 'commissioningCommLoop.networkSwitch' },
  { titleKey: 'commissioningCommLoop.protocolConverter.title', fields: PROTOCOL_CONVERTER_FIELDS, sectionKey: 'protocolConverter', translationPrefix: 'commissioningCommLoop.protocolConverter' },
  { titleKey: 'commissioningCommLoop.cellularRouter.title', fields: CELLULAR_ROUTER_FIELDS, sectionKey: 'cellularRouter', translationPrefix: 'commissioningCommLoop.cellularRouter' },
  { titleKey: 'commissioningCommLoop.masterRadio.title', fields: MASTER_RADIO_FIELDS, sectionKey: 'masterRadio', translationPrefix: 'commissioningCommLoop.masterRadio' },
  { titleKey: 'commissioningCommLoop.slaveRadio.title', fields: SLAVE_RADIO_FIELDS, sectionKey: 'slaveRadio', translationPrefix: 'commissioningCommLoop.slaveRadio' },
];

const INFO_SECTION_KEYS = ['projectInfo', 'networkSwitch', 'protocolConverter'];
export const CELLULAR_RADIO_SECTION_KEYS = ['cellularRouter', 'masterRadio', 'slaveRadio'];

export default function CommLoopInfo (props) {
  const { data, onChange, readOnly, sectionKeys } = props;
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);

  const keys = sectionKeys || INFO_SECTION_KEYS;
  const sections = ALL_SECTIONS.filter((s) => keys.includes(s.sectionKey));

  return (
    <div>
      {sections.map((s) => (
        <InfoSection
          key={s.sectionKey}
          titleKey={s.titleKey}
          fields={s.fields}
          sectionKey={s.sectionKey}
          translationPrefix={s.translationPrefix}
          data={data}
          onChange={onChange}
          readOnly={readOnly}
          isMobile={isMobile}
        />
      ))}
    </div>
  );
}

InfoSection.propTypes = {
  titleKey: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  sectionKey: PropTypes.string.isRequired,
  translationPrefix: PropTypes.string.isRequired,
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  isMobile: PropTypes.bool,
};

CommLoopInfo.propTypes = {
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  sectionKeys: PropTypes.arrayOf(PropTypes.string),
};

CommLoopInfo.defaultProps = {
  readOnly: false,
};
